export const ALIGNER_CONSTANTS = {
  MAX_TRANSCRIPT_WINDOW: 20,
  MAX_DISTANCE_RATIO: 0.45,
  MAX_JUMP: 15,
  ANCHOR_DECAY: 0.015,
} as const;

const { MAX_TRANSCRIPT_WINDOW, MAX_DISTANCE_RATIO, MAX_JUMP, ANCHOR_DECAY } = ALIGNER_CONSTANTS;

export function levenshteinDistance(a: string, b: string): number {
  const m = a.length;
  const n = b.length;

  if (m === 0) return n;
  if (n === 0) return m;

  // Early exit: if length difference > threshold, return early
  if (Math.abs(m - n) > 2) return Math.abs(m - n);

  const prev = new Array<number>(n + 1);
  const curr = new Array<number>(n + 1);

  for (let j = 0; j <= n; j++) prev[j] = j;

  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j <= n; j++) prev[j] = curr[j];
  }

  return prev[n];
}

export function fuzzyMatch(a: string, b: string): boolean {
  return levenshteinDistance(a.toLowerCase(), b.toLowerCase()) <= 1;
}

/**
 * Word-level Levenshtein: edit distance on arrays of words using per-word fuzzy comparison.
 */
export function wordLevenshtein(a: string[], b: string[]): number {
  const m = a.length;
  const n = b.length;

  if (m === 0) return n;
  if (n === 0) return m;

  const prev = new Array<number>(n + 1);
  const curr = new Array<number>(n + 1);

  for (let j = 0; j <= n; j++) prev[j] = j;

  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = fuzzyMatch(a[i - 1], b[j - 1]) ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j <= n; j++) prev[j] = curr[j];
  }

  return prev[n];
}

export interface AlignmentResult {
  cursorPosition: number;
  sectionIndex: number;
  sectionProgress: number;
  computeMs: number;
  boundaryJustCrossed: number | null;
}

export interface MatchDiagnostics {
  bestRawDist: number;
  bestAdjustedDist: number;
  ratio: number;
  searchStart: number;
  searchEnd: number;
  bestEnd: number;
  bufferStartCursor: number;
  accepted: boolean;
  jump: number;
  jumpRejected: boolean;
  isSameSegment: boolean;
  computeMs: number;
}

export interface AlignerDebugSnapshot {
  cursorPosition: number;
  sectionIndex: number;
  sectionProgress: number;
  transcriptBuffer: string[];
  bufferStartCursor: number;
  avgWpm: number | null;
  sectionWpms: number[];
  anchor: number | null;
  scriptWordCount: number;
  lastMatch: MatchDiagnostics | null;
}

function normalizeWord(word: string): string {
  return word.toLowerCase().replace(/[^a-z0-9']/g, '');
}

export class FuzzyAligner {
  private readonly scriptWords: string[];
  private readonly normalizedScript: string[];
  private readonly sectionBoundaries: number[];
  private readonly now: () => number;

  private _cursorPosition = 0;
  private _sectionIndex = 0;
  private _sectionWpms: number[] = [];
  private transcriptBuffer: string[] = [];
  private bufferStartCursor = 0; // cursor position when buffer was first populated
  private segmentStartCursor = 0; // cursor position when current segment began
  private segmentFirstWord: string | null = null; // first word of current segment (survives trimming)
  private sectionStartTime: number | null = null;
  private startTime: number | null = null;
  private _lastMatch: MatchDiagnostics | null = null;

  constructor(
    scriptWords: string[],
    sectionBoundaries: number[],
    options?: { now?: () => number },
  ) {
    this.scriptWords = scriptWords;
    this.normalizedScript = scriptWords.map(normalizeWord);
    this.sectionBoundaries = sectionBoundaries;
    this.now = options?.now ?? (() => performance.now());
  }

  feedWords(words: string[]): AlignmentResult {
    const t0 = this.now();

    if (this.startTime === null) {
      this.startTime = t0;
    }
    if (this.sectionStartTime === null) {
      this.sectionStartTime = t0;
    }

    let boundaryJustCrossed: number | null = null;
    let isSameSegment = false;

    // Buffer management: detect same-segment updates vs new segments.
    // Chrome's interim results grow within a segment ("the" → "the quick" → "the quick brown").
    // When a segment finalizes, a new one starts with fresh words.
    //
    // Strategy: always replace with the incoming words, but track the previous segment's
    // last words. When a new short segment arrives, prepend context from the previous one.
    const normalized: string[] = [];
    for (const word of words) {
      const n = normalizeWord(word);
      if (n) normalized.push(n);
    }

    if (normalized.length > 0) {
      // Detect same-segment update vs new segment.
      // Chrome interim results grow within a segment with a shared prefix.
      // A new segment starts with completely different words.
      // Compare against segmentFirstWord (not buffer[0]) since trimming shifts the buffer.
      isSameSegment =
        this.segmentFirstWord !== null &&
        normalized.length >= this.transcriptBuffer.length &&
        normalized[0] === this.segmentFirstWord;

      if (isSameSegment) {
        // Same segment growing — replace buffer, keep segmentStartCursor
        this.transcriptBuffer = normalized;
      } else {
        // New segment — reset both cursors to current cursor position
        this.transcriptBuffer = normalized;
        this.segmentStartCursor = this._cursorPosition;
        this.bufferStartCursor = this._cursorPosition;
        this.segmentFirstWord = normalized[0];
      }

      if (this.transcriptBuffer.length > MAX_TRANSCRIPT_WINDOW) {
        const trimCount = this.transcriptBuffer.length - MAX_TRANSCRIPT_WINDOW;
        // Compute from segment origin to avoid accumulation drift
        this.bufferStartCursor = this.segmentStartCursor + trimCount;
        this.transcriptBuffer = this.transcriptBuffer.slice(trimCount);
      }
    }

    if (this.scriptWords.length === 0 || this.transcriptBuffer.length === 0) {
      const t1 = this.now();
      return {
        cursorPosition: this._cursorPosition,
        sectionIndex: this._sectionIndex,
        sectionProgress: this.sectionProgress,
        computeMs: t1 - t0,
        boundaryJustCrossed,
      };
    }

    // Use the full transcript (capped) for comparison
    const recentTranscript = this.transcriptBuffer.slice(-MAX_TRANSCRIPT_WINDOW);

    // Start comparison from where the buffer's words begin in the script,
    // not from the cursor position. The buffer may contain words from before
    // the cursor (Chrome sends the full segment transcript including already-matched words).
    const searchStart = Math.min(this.bufferStartCursor, this._cursorPosition);
    const searchEnd = Math.min(
      this.normalizedScript.length,
      searchStart + recentTranscript.length * 2 + 10,
    );

    // Compute anchor once outside the loop
    const anchor = this.getAnchor();

    // When anchor is significantly ahead of cursor, also search near the anchor.
    // This handles catch-up scenarios where the cursor fell behind the speaker.
    // Center the search window around the anchor so the optimal match isn't buried
    // behind a long prefix of non-matching words.
    const anchorSearchStart =
      anchor !== null && anchor - this._cursorPosition > MAX_JUMP
        ? Math.max(this._cursorPosition, Math.floor(anchor))
        : null;
    const anchorSearchEnd =
      anchorSearchStart !== null
        ? Math.min(
            this.normalizedScript.length,
            anchorSearchStart + recentTranscript.length * 2 + 10,
          )
        : null;

    // For each candidate end position, compare using word-level Levenshtein.
    // Word-level scoring is more robust to speech recognition errors: a word
    // substitution costs 1 (not 5-10 characters), and per-word fuzzy matching
    // tolerates minor transcription errors within individual words.
    let bestEnd = -1;
    let bestDistance = Infinity;
    let bestRawDist = Infinity;
    let bestSearchStart = searchStart;

    // Search from buffer/cursor position
    for (let end = searchStart + 1; end <= searchEnd; end++) {
      const scriptSlice = this.normalizedScript.slice(searchStart, end);
      const dist = wordLevenshtein(recentTranscript, scriptSlice);

      let adjustedDist = dist;
      if (anchor !== null) {
        const midpoint = searchStart + (end - searchStart) / 2;
        const anchorDistance = Math.abs(midpoint - anchor);
        adjustedDist = dist + ANCHOR_DECAY * anchorDistance;
      }

      if (adjustedDist < bestDistance) {
        bestDistance = adjustedDist;
        bestRawDist = dist;
        bestEnd = end;
        bestSearchStart = searchStart;
      }
    }

    // Also search near anchor position if it's significantly ahead
    if (anchorSearchStart !== null && anchorSearchEnd !== null && anchor !== null) {
      for (let end = anchorSearchStart + 1; end <= anchorSearchEnd; end++) {
        const scriptSlice = this.normalizedScript.slice(anchorSearchStart, end);
        const dist = wordLevenshtein(recentTranscript, scriptSlice);

        let adjustedDist = dist;
        const midpoint = anchorSearchStart + (end - anchorSearchStart) / 2;
        const anchorDistance = Math.abs(midpoint - anchor);
        adjustedDist = dist + ANCHOR_DECAY * anchorDistance;

        if (adjustedDist < bestDistance) {
          bestDistance = adjustedDist;
          bestRawDist = dist;
          bestEnd = end;
          bestSearchStart = anchorSearchStart;
        }
      }
    }

    let accepted = false;
    let ratio = 1;
    let jump = 0;
    let jumpRejected = false;

    if (bestEnd > 0) {
      // Compute word-level ratio: edit distance / max word count
      const scriptSliceLen = bestEnd - bestSearchStart;
      const maxLen = Math.max(recentTranscript.length, scriptSliceLen);
      ratio = maxLen > 0 ? bestRawDist / maxLen : 1;

      if (ratio <= MAX_DISTANCE_RATIO) {
        const newCursor = Math.min(bestEnd - 1, this.scriptWords.length - 1);

        if (newCursor >= this._cursorPosition) {
          jump = newCursor - this._cursorPosition;
          // Allow large jumps when: (a) jump is small, (b) near-perfect match,
          // or (c) WPM anchor independently confirms the destination position
          const anchorConfirmed = anchor !== null && Math.abs(newCursor - anchor) <= MAX_JUMP;
          if (jump <= MAX_JUMP || ratio <= 0.1 || anchorConfirmed) {
            accepted = true;
            const prevSection = this._sectionIndex;
            this._cursorPosition = newCursor;
            this.updateSectionIndex();

            if (this._sectionIndex !== prevSection) {
              const now = this.now();
              /* v8 ignore next 6 -- timing guards covered by WPM integration tests */
              if (this.sectionStartTime !== null) {
                const elapsed = now - this.sectionStartTime;
                if (elapsed > 0) {
                  const sectionStart = this.sectionBoundaries[prevSection] ?? 0;
                  const sectionEnd =
                    this.sectionBoundaries[prevSection + 1] ?? this.scriptWords.length;
                  const sectionWordCount = sectionEnd - sectionStart;
                  const wpm = (sectionWordCount / elapsed) * 60000;
                  this._sectionWpms.push(wpm);
                }
              }
              this.sectionStartTime = this.now();
              boundaryJustCrossed = this._sectionIndex;
            }
          } else {
            jumpRejected = true;
          }
        }
      }
    }

    const t1 = this.now();

    this._lastMatch = {
      bestRawDist,
      bestAdjustedDist: bestDistance,
      ratio,
      searchStart: bestSearchStart,
      searchEnd: anchorSearchEnd ?? searchEnd,
      bestEnd,
      bufferStartCursor: this.bufferStartCursor,
      accepted,
      jump,
      jumpRejected,
      isSameSegment,
      computeMs: t1 - t0,
    };

    return {
      cursorPosition: this._cursorPosition,
      sectionIndex: this._sectionIndex,
      sectionProgress: this.sectionProgress,
      computeMs: t1 - t0,
      boundaryJustCrossed,
    };
  }

  get cursorPosition(): number {
    return this._cursorPosition;
  }

  get sectionIndex(): number {
    return this._sectionIndex;
  }

  get sectionProgress(): number {
    if (this.scriptWords.length === 0) return 0;

    const sectionStart = this.sectionBoundaries[this._sectionIndex] ?? 0;
    const sectionEnd =
      this._sectionIndex + 1 < this.sectionBoundaries.length
        ? this.sectionBoundaries[this._sectionIndex + 1]
        : this.scriptWords.length;
    const sectionLen = sectionEnd - sectionStart;
    if (sectionLen === 0) return 0;

    const progress = (this._cursorPosition - sectionStart) / sectionLen;
    return Math.min(1, Math.max(0, progress));
  }

  get avgWpm(): number | null {
    if (this._sectionWpms.length === 0) return null;
    const sum = this._sectionWpms.reduce((a, b) => a + b, 0);
    return sum / this._sectionWpms.length;
  }

  get sectionWpms(): number[] {
    return [...this._sectionWpms];
  }

  getAnchor(): number | null {
    const wpm = this.avgWpm;
    if (wpm === null || this.startTime === null) return null;

    const elapsed = this.now() - this.startTime;
    return (wpm / 60000) * elapsed;
  }

  getDebugSnapshot(): AlignerDebugSnapshot {
    return {
      cursorPosition: this._cursorPosition,
      sectionIndex: this._sectionIndex,
      sectionProgress: this.sectionProgress,
      transcriptBuffer: [...this.transcriptBuffer],
      bufferStartCursor: this.bufferStartCursor,
      avgWpm: this.avgWpm,
      sectionWpms: [...this._sectionWpms],
      anchor: this.getAnchor(),
      scriptWordCount: this.scriptWords.length,
      lastMatch: this._lastMatch ? { ...this._lastMatch } : null,
    };
  }

  setCursorPosition(position: number): void {
    this._cursorPosition = Math.max(0, Math.min(position, this.scriptWords.length - 1));
    this.updateSectionIndex();
    this.transcriptBuffer = [];
    this.bufferStartCursor = this._cursorPosition;
    this.segmentStartCursor = this._cursorPosition;
    this.segmentFirstWord = null;
  }

  reset(): void {
    this._cursorPosition = 0;
    this._sectionIndex = 0;
    this._sectionWpms = [];
    this.transcriptBuffer = [];
    this.bufferStartCursor = 0;
    this.segmentStartCursor = 0;
    this.segmentFirstWord = null;
    this.sectionStartTime = null;
    this.startTime = null;
    this._lastMatch = null;
  }

  private updateSectionIndex(): void {
    let newIndex = 0;
    for (let i = 0; i < this.sectionBoundaries.length; i++) {
      if (this._cursorPosition >= this.sectionBoundaries[i]) {
        newIndex = i;
      } else {
        break;
      }
    }
    this._sectionIndex = newIndex;
  }
}
